import { Check } from 'lucide-react';

import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';

export type RowState = 'active' | 'completed' | 'upcoming';

function StepBadge({ state, index }: { state: RowState; index: number }) {
  if (state === 'completed') {
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-forest text-text-primary-invert">
        <Check className="size-3.5" />
      </span>
    );
  }
  return (
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums',
        state === 'active'
          ? 'bg-text-primary text-text-primary-invert'
          : 'bg-background-secondary text-text-tertiary'
      )}
    >
      {index + 1}
    </span>
  );
}

/**
 * One row of the Learn-the-basics vertical accordion:
 *   - active    → expanded card with its control (children)
 *   - completed → collapsed, ticked, strike-through, clickable to re-open
 *   - upcoming  → collapsed, dimmed, not interactive
 */
export function LearnBasicsAccordionRow({
  index,
  title,
  subtitle,
  state,
  onReopen,
  children,
}: {
  index: number;
  title: string;
  subtitle: string;
  state: RowState;
  onReopen?: () => void;
  children?: React.ReactNode;
}) {
  if (state === 'active') {
    return (
      <div className="rounded-xl border border-accent-forest/20 bg-surface px-3.5 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <StepBadge state={state} index={index} />
          <TypographyP2 className="font-semibold text-text-primary">{title}</TypographyP2>
        </div>
        <TypographyCaption className="mt-1 block pl-9 text-text-tertiary">
          {subtitle}
        </TypographyCaption>
        <div className="mt-3">{children}</div>
      </div>
    );
  }

  const content = (
    <>
      <StepBadge state={state} index={index} />
      <span
        className={cn(
          'flex-1 text-sm',
          state === 'completed'
            ? 'text-text-tertiary line-through decoration-text-tertiary'
            : 'text-text-secondary'
        )}
      >
        {title}
      </span>
    </>
  );

  if (state === 'completed' && onReopen) {
    return (
      <button
        type="button"
        onClick={onReopen}
        className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface px-3.5 py-3 text-left transition-colors hover:border-accent-forest/30"
      >
        {content}
      </button>
    );
  }

  return (
    <div className="flex w-full items-center gap-3 rounded-xl px-3.5 py-3 opacity-50">
      {content}
    </div>
  );
}
